import { useEffect, useState } from "react";
import { FaSkull, FaPlay } from "react-icons/fa";

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const EVENT_DATE = new Date("2026-10-03T18:00:00");

function getTimeLeft(): TimeLeft {
  const now = new Date();
  const diff = EVENT_DATE.getTime() - now.getTime();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

const handleNavClick = (href: string) => {
  const id = href.replace("#", "");
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(getTimeLeft());

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(getTimeLeft());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="home" className="hero-section" style={{ minHeight: "100vh" }}>
      {/* Background */}
      <div className="hero-section__bg" />

      {/* Fog Layers */}
      <div className="fog-container">
        <div className="fog-layer fog-1" />
        <div className="fog-layer fog-2" />
        <div className="fog-layer fog-3" />
      </div>

      {/* Content */}
      <div className="hero-section__content" style={{ paddingTop: "80px" }}>
        {/* Pre-label */}
        <div
          className="section-label"
          style={{
            justifyContent: "center",
            marginBottom: "24px",
            opacity: 0,
            animation: "floatIn 0.7s ease 0.2s forwards",
          }}
        >
          3rd Annual Film Festival
        </div>

        {/* Main Headline */}
        <h1
          className="horror-heading"
          style={{
            fontSize: "clamp(3.5rem, 14vw, 10rem)",
            lineHeight: 0.85,
            marginBottom: "24px",
            opacity: 0,
            animation: "floatIn 0.8s ease 0.4s forwards",
          }}
        >
          Midwest
          <br />
          Fear Fest
          <br />
          <span
            style={{
              fontFamily: '"Bebas Neue", cursive',
              fontSize: "0.45em",
              letterSpacing: "0.25em",
              background: "none",
              WebkitTextFillColor: "rgba(232,232,232,0.9)",
              color: "rgba(232,232,232,0.9)",
            }}
          >
            2026
          </span>
        </h1>

        {/* Subheadline */}
        <p
          style={{
            fontFamily: '"Bebas Neue", cursive',
            fontSize: "clamp(1rem, 3vw, 1.5rem)",
            letterSpacing: "0.2em",
            color: "rgba(232,232,232,0.7)",
            marginBottom: "40px",
            opacity: 0,
            animation: "floatIn 0.7s ease 0.6s forwards",
          }}
        >
          1 Night of Film &nbsp;|&nbsp; Oct 3, Indianapolis
        </p>

        {/* Countdown */}
        <div
          style={{
            marginBottom: "48px",
            opacity: 0,
            animation: "floatIn 0.7s ease 0.8s forwards",
          }}
        >
          <p
            style={{
              fontFamily: '"Bebas Neue", cursive',
              fontSize: "0.7rem",
              letterSpacing: "0.3em",
              color: "#888899",
              marginBottom: "16px",
            }}
          >
            COUNTING DOWN TO THE FESTIVAL
          </p>
          <div className="countdown-wrapper">
            {(
              [
                { value: timeLeft.days, label: "Days" },
                { value: timeLeft.hours, label: "Hours" },
                { value: timeLeft.minutes, label: "Minutes" },
                { value: timeLeft.seconds, label: "Seconds" },
              ] as { value: number; label: string }[]
            ).map(({ value, label }) => (
              <div className="countdown-unit" key={label}>
                <span className="countdown-number">{pad(value)}</span>
                <span className="countdown-label">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTAs */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            justifyContent: "center",
            flexWrap: "wrap",
            opacity: 0,
            animation: "floatIn 0.7s ease 1s forwards",
          }}
        >
          <a
            href="https://kankanindy.com/events/midwestfearfest/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ fontSize: "1.05rem", padding: "16px 40px" }}
          >
            <FaSkull style={{ fontSize: "0.85em" }} />
            Get Your Tickets
          </a>
          <a
            href="#selections"
            className="btn-secondary"
            style={{ fontSize: "1.05rem", padding: "16px 40px" }}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("#selections");
            }}
          >
            <FaPlay style={{ fontSize: "0.75em" }} />
            View Selections
          </a>
        </div>

        {/* Location tag */}
        <p
          style={{
            marginTop: "40px",
            fontSize: "0.8rem",
            letterSpacing: "0.15em",
            color: "rgba(136,136,153,0.7)",
            opacity: 0,
            animation: "floatIn 0.7s ease 1.2s forwards",
          }}
        >
          Kan-Kan Cinema &amp; Brasserie · Windsor Park · Indianapolis, IN
        </p>
      </div>

      {/* SVG Wave Divider */}
      <div className="divider-wave-bottom">
        <svg
          viewBox="0 0 1440 80"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            d="M0,40 C360,90 720,0 1080,60 C1260,80 1380,30 1440,40 L1440,80 L0,80 Z"
            fill="#0f0f14"
          />
        </svg>
      </div>
    </section>
  );
}
